import React, { useEffect, useRef, useState } from 'react';
import { IconButton, Button, Badge, Stack, useMediaQuery, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
	GridPreferencePanelsValue,
	GridState,
	useGridApiContext,
	useGridSelector,
} from '@mui/x-data-grid-pro';
import { RiCloseLine, RiDownload2Line, RiFilter3Line, RiLayoutColumnLine } from 'react-icons/ri';
import { getHiddenColumns, getPinnedColumns } from './utils';
import { isEqual } from 'lodash';
import DataGridSelectedRowsActions, {
	DataGridSelectedRowsActionsProps,
} from './DataGridSelectedRowsActions';
import produce from 'immer';
import { useTranslation } from 'services/localization';
import { IconType } from 'react-icons';
import { useDispatch, useSelector } from 'react-redux';
import * as ViewSelectors from 'selectors/ViewSelectors';
import * as ViewActions from 'actions/ViewActions';
import { PersistedDataGridState } from './types';

export interface DataGridToolbarProps<RowType extends object> {
	persistKey?: string;
	exportFileName?: string;
	selectedRowsActions?: DataGridSelectedRowsActionsProps<RowType>;
	toolbarActions?: DataGridToolbarAction[];
	dateRange?: string;
}

export interface DataGridToolbarAction {
	label: string;
	handler: () => void;
	icon: IconType;
}

const DataGridToolbar = <RowType extends object>(props: DataGridToolbarProps<RowType>) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { persistKey, exportFileName, selectedRowsActions, toolbarActions, dateRange } = props;

	const apiRef = useGridApiContext();

	const filterModel = useGridSelector(apiRef, (state: GridState) => state.filter.filterModel);
	const columns = useGridSelector(apiRef, (state: GridState) => state.columns);
	const initialColumnOrder = useRef(columns.all);
	const pinnedColumns = useGridSelector(apiRef, (state: GridState) => state.pinnedColumns);
	const selectedRows = useGridSelector(apiRef, (state: GridState) =>
		Array.isArray(state.selection)
			? state.selection.map((id) => String(id))
			: [String(state.selection)],
	);
	const rowCount = useGridSelector(apiRef, (state: GridState) => state.rows.ids.length);

	const loading = useGridSelector(apiRef, (state: GridState) => state.rows.loading);

	const [isPersistedStateLoaded, setPersistedStateLoaded] = useState<boolean>(false);
	const persistedDataGridStates = useSelector(ViewSelectors.dataGridStates);

	const visibleColumns = columns.all.filter((c) => !columns.lookup[c].hide);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));

	const activeFiltersCount = filterModel.items.length;
	const hasActiveFilters = !!activeFiltersCount;
	const hasHiddenColumns = columns.all.length !== visibleColumns.length;
	const hasPinnedColumns = (() => {
		return (
			!!pinnedColumns.left?.filter((col) => col !== '__check__')?.length ||
			!!pinnedColumns.right?.length
		);
	})();
	const hasSelectedRows = !!selectedRows.length;

	const hasColumnOrderChanged = !isEqual(columns.all, initialColumnOrder.current);

	useEffect(() => {
		const loadPersistedState = (key: string) => {
			const data = persistedDataGridStates[key];
			setPersistedStateLoaded(true);
			if (!!data) {
				apiRef.current.setState((state) => ({
					...state,
					columns: produce(state.columns, (columnsState) => {
						columnsState.all.forEach((col) => {
							const isHidden = !!data.hiddenColumns?.includes(col);
							columnsState.lookup[col].hide = isHidden;
						});
					}),
				}));
				apiRef.current.setFilterModel(data.filterModel);
				apiRef.current.setSortModel(data.sortModel);
				apiRef.current.setPageSize(data.pageSize);
				apiRef.current.setPinnedColumns(getPinnedColumns(data.pinnedColumns));
			}
		};
		if (!!persistKey && !isPersistedStateLoaded && rowCount > 0) {
			loadPersistedState(persistKey);
		}
	}, [apiRef, persistKey, persistedDataGridStates, isPersistedStateLoaded, rowCount]);

	useEffect(() => {
		return () => {
			if (!!persistKey) {
				// eslint-disable-next-line react-hooks/exhaustive-deps
				const { state } = apiRef.current;
				const persistedState: PersistedDataGridState = {
					filterModel: state.filter.filterModel,
					sortModel: state.sorting.sortModel,
					pinnedColumns: getPinnedColumns(state.pinnedColumns),
					hiddenColumns: getHiddenColumns(state.columns),
					pageSize: state.pagination.pageSize,
				};

				dispatch(ViewActions.setDataGridState(persistKey, persistedState));
			}
		};
	}, [apiRef, persistKey, dispatch]);

	const handleReset = () => {
		apiRef.current.setState((state) => ({
			...state,
			pinnedColumns: getPinnedColumns({}),
			columns: produce(state.columns, (columnsState) => {
				columnsState.all = initialColumnOrder.current;
				columnsState.all.forEach((col) => {
					columnsState.lookup[col].hide = false;
				});
			}),
		}));
		apiRef.current.setFilterModel({ items: [] });
	};

	const renderColumnsButton = () => {
		const label = t('dataGrid.toolbarColumns');
		const onClick = () => apiRef.current.showPreferences(GridPreferencePanelsValue.columns);

		return isMobile ? (
			<Tooltip title={label}>
				<IconButton onClick={onClick} sx={{ mr: 1 }}>
					<Badge
						color="primary"
						badgeContent={hasHiddenColumns ? visibleColumns.length : null}
						showZero={true}
					>
						<RiLayoutColumnLine size={20} color={theme.palette.primary.main} />
					</Badge>
				</IconButton>
			</Tooltip>
		) : (
			<Button
				color="primary"
				variant="text"
				startIcon={<RiLayoutColumnLine size={20} />}
				sx={{ mr: 1 }}
				onClick={onClick}
			>
				{label} {hasHiddenColumns ? ` (${visibleColumns.length})` : ''}
			</Button>
		);
	};

	const renderFiltersButton = () => {
		const label = t('dataGrid.toolbarFilters');
		const onClick = () => apiRef.current.showPreferences(GridPreferencePanelsValue.filters);

		return isMobile ? (
			<Tooltip title={label}>
				<IconButton onClick={onClick} sx={{ mr: 1 }}>
					<Badge
						color="primary"
						badgeContent={hasActiveFilters ? activeFiltersCount : null}
						showZero={true}
					>
						<RiFilter3Line size={20} color={theme.palette.primary.main} />
					</Badge>
				</IconButton>
			</Tooltip>
		) : (
			<Button
				color="primary"
				variant="text"
				startIcon={<RiFilter3Line size={20} />}
				sx={{ mr: 1 }}
				onClick={onClick}
			>
				{label} {hasActiveFilters ? ` (${activeFiltersCount})` : ''}
			</Button>
		);
	};

	const renderClearButton = () => {
		if (!hasActiveFilters && !hasHiddenColumns && !hasPinnedColumns && !hasColumnOrderChanged)
			return null;

		const label = t('common:actions.reset');

		return isMobile ? (
			<Tooltip title={label}>
				<IconButton onClick={handleReset} sx={{ mr: 1 }}>
					<RiCloseLine size={20} />
				</IconButton>
			</Tooltip>
		) : (
			<Button
				color="secondary"
				variant="text"
				startIcon={<RiCloseLine size={20} />}
				onClick={handleReset}
			>
				{label}
			</Button>
		);
	};

	const renderToolbarActions = () => {
		return (toolbarActions ?? []).map((action) => {
			const Icon = action.icon;

			return isMobile ? (
				<Tooltip key={action.label} title={action.label}>
					<IconButton onClick={action.handler} disabled={hasSelectedRows}>
						<Icon size={20} />
					</IconButton>
				</Tooltip>
			) : (
				<Button
					key={action.label}
					onClick={action.handler}
					color="secondary"
					variant="text"
					startIcon={<Icon size={20} />}
					disabled={hasSelectedRows}
				>
					{action.label}
				</Button>
			);
		});
	};

	const renderExportButton = () => {
		const label =
			dateRange === 'all'
				? t('common:actions.exportAll', 'Export All')
				: t('dataGrid.toolbarExport');

		const onClick = () => apiRef.current.exportDataAsCsv({ fileName: exportFileName });

		return isMobile ? (
			<Tooltip title={label}>
				<IconButton onClick={onClick}>
					<RiDownload2Line size={20} />
				</IconButton>
			</Tooltip>
		) : (
			<Button
				onClick={onClick}
				color="secondary"
				variant="text"
				startIcon={<RiDownload2Line size={20} />}
				disabled={loading}
			>
				{label}
			</Button>
		);
	};

	return (
		<>
			<Stack p={1} direction="row" justifyContent="space-between" alignItems="center">
				<Stack direction="row" justifyContent="flex-start" alignItems="center">
					{renderColumnsButton()}
					{renderFiltersButton()}
					{renderClearButton()}
				</Stack>
				<Stack direction="row" justifyContent="flex-end" alignItems="center">
					{renderToolbarActions()}
					{renderExportButton()}
				</Stack>
			</Stack>
			{!!selectedRowsActions && (
				<DataGridSelectedRowsActions exportFileName={exportFileName} {...selectedRowsActions} />
			)}
		</>
	);
};

export default DataGridToolbar;
